import { toDocSearchParams } from '@affine/core/modules/navigation';
import {
  type AggregateInput,
  type IndexerAggregateQuery,
  indexerAggregateQuery,
  type IndexerSearchDocsQuery,
  indexerSearchDocsQuery,
  type MailboxRootDocIdsQuery,
  mailboxRootDocIdsQuery,
  SearchTable,
} from '@affine/graphql';
import type {
  AggregateOptions,
  AggregateResult,
  IndexerPreferOptions,
  IndexerSchema,
  IndexerSyncState,
  Query,
  SearchOptions,
  SearchResult,
} from '@affine/nbstore';
import type { ReferenceParams } from '@blocksuite/affine/model';
import {
  createServerDatabaseRootDocId,
  parseServerDatabaseRowShardDocId,
} from '@streamlain/database-identity';
import { fromPromise, LiveData, Service } from '@toeverything/infra';
import { isEmpty, omit } from 'lodash-es';
import {
  catchError,
  combineLatest,
  concat,
  defer,
  filter,
  finalize,
  firstValueFrom,
  from,
  map,
  type Observable,
  of,
  ReplaySubject,
  share,
  Subject,
  type Subscription,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { z } from 'zod';

import type { WorkspaceServerService } from '../../cloud';
import type { DocsService } from '../../doc/services/docs';
import type { PermissionSnapshotStore } from '../../permissions/stores/permission-snapshot';
import type { WorkspaceLocalCache, WorkspaceService } from '../../workspace';
import { mergeDatabaseSearchResults } from './database-search-merge';
import {
  createDocsSearchAggregateOptions,
  createServerAggregateQuery,
  mapDocsSearchAggregateBuckets,
  mapLocalDocFileNameMatches,
  mapLocalDocSimilarMatches,
  mapLocalDocTitleMatches,
  mergeDocsSearchAggregateItems,
} from './docs-search-aggregate';
import {
  mapRemoteSearchDocsForQuickSearch,
  mergeRemoteDocsSearchResults,
} from './docs-search-remote';
import type { DocsSearchResult } from './source-navigation';

export interface DocsSearchOptions {
  readonly prefer?: IndexerPreferOptions;
  readonly includeTechnicalSources?: boolean;
  /** Partitions the local index answers; the server search leaves them out. */
  readonly excludedPartitions?: readonly number[];
  /**
   * Pages within the shared typo budget of the query (search-extract), not
   * exact hits: locally by title and catalog file name, on the server by
   * content.
   */
  readonly similar?: boolean;
}

type HiddenTechnicalDocIds = ReadonlySet<string> | null;

/** Mailbox pages hidden from search, as the server named them last. */
const HIDDEN_TECHNICAL_DOC_IDS_KEY = 'search-hidden-technical-doc-ids';

export function filterHiddenTechnicalDocs<T extends { docId: string }>(
  items: T[],
  hiddenTechnicalDocIds: HiddenTechnicalDocIds,
  options: DocsSearchOptions = {}
): T[] {
  if (options.includeTechnicalSources) return items;
  if (hiddenTechnicalDocIds === null) return [];
  if (hiddenTechnicalDocIds.size === 0) return items;
  return items.filter(item => !hiddenTechnicalDocIds.has(item.docId));
}

/**
 * Keeps hits of pages the user may read: pages of the workspace's doc list
 * (the readable pages) whose offline grant has not run out. The local index
 * can still hold pages whose access was revoked or expired; they stay in the
 * index and come back once access is renewed.
 */
export function filterReadableDocs<T extends { docId: string }>(
  items: T[],
  isReadable: (docId: string) => boolean
): T[] {
  return items.filter(item => isReadable(item.docId));
}

/**
 * Equal for equal queries: object keys sorted, unset keys left out, and
 * Infinity and NaN kept apart from null.
 */
function readKey(value: unknown): string {
  if (typeof value === 'number' && !Number.isFinite(value)) {
    return String(value);
  }
  if (value === undefined) return 'undefined';
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(readKey).join(',')}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .filter(key => record[key] !== undefined)
    .sort()
    .map(key => `${JSON.stringify(key)}:${readKey(record[key])}`)
    .join(',')}}`;
}

export class DocsSearchService extends Service {
  private hiddenTechnicalDocIdsPromise: Promise<HiddenTechnicalDocIds> | null =
    null;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly docsService: DocsService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly permissionSnapshotStore: PermissionSnapshotStore,
    private readonly localCache: WorkspaceLocalCache
  ) {
    super();
  }

  /** Reads the readable pages once per search answer. */
  private readableDocs() {
    const docIds = new Set(
      this.docsService.list.docs$.value.map(doc => doc.id)
    );
    return (docId: string) =>
      docIds.has(docId) &&
      !this.permissionSnapshotStore.isGrantExpired('doc', docId);
  }

  get indexer() {
    return this.workspaceService.workspace.engine.indexer;
  }

  readonly indexerState$ = LiveData.from(this.indexer.state$, {
    indexing: 0,
    errorMessage: null,
  } as IndexerSyncState);

  /**
   * True from a failed read of the local index until the index answers again.
   * Readers get an empty result meanwhile.
   */
  readonly indexUnavailable$ = new LiveData(false);

  /**
   * True when the last local page search hit the word-part scan cap: some
   * matching pages may be missing from the answer.
   */
  readonly localTruncated$ = new LiveData(false);

  /** Pages whose access was not confirmed in time; search hides them. */
  readonly expiredDocCount$ = this.permissionSnapshotStore.expiredGrants$.map(
    grants => {
      let count = 0;
      for (const grant of grants.values()) {
        if (grant.resourceType === 'doc') count++;
      }
      return count;
    }
  );

  private readonly indexStateChanges$ = new Subject<IndexerSyncState>();
  private indexStateSubscription: Subscription | null = null;

  /**
   * The only way to read the local index. A failed read does not reach the
   * reader: it gets `whenUnavailable` and reads again once the index reports a
   * state without an error. A new reader on a failed run of the index fails at
   * once, so the next read waits for a state change instead of repeating.
   */
  private localRead$<T>(
    read: () => Observable<T>,
    whenUnavailable: () => T
  ): Observable<T> {
    return defer(() => {
      this.indexStateSubscription ??= this.indexer.state$.subscribe(state =>
        this.indexStateChanges$.next(state)
      );
      return read();
    }).pipe(
      tap(() => this.setIndexUnavailable(false)),
      catchError(() => {
        this.setIndexUnavailable(true);
        return concat(
          of(whenUnavailable()),
          this.indexStateChanges$.pipe(
            filter(state => state.errorMessage === null),
            take(1),
            switchMap(() => this.localRead$(read, whenUnavailable))
          )
        );
      })
    );
  }

  private setIndexUnavailable(unavailable: boolean) {
    if (this.indexUnavailable$.value !== unavailable) {
      this.indexUnavailable$.value = unavailable;
    }
  }

  /** Open index reads by query; a read ends with its last reader. */
  private readonly sharedReads = new Map<string, Observable<unknown>>();

  /**
   * Readers of the same query share one read of the index. Each reader keeps
   * its own localRead$ around it, so a failure still reaches every reader.
   */
  private sharedRead$<T>(key: string, read: () => Observable<T>) {
    const open$ = this.sharedReads.get(key);
    if (open$) return open$ as Observable<T>;
    const shared$: Observable<T> = defer(read).pipe(
      finalize(() => {
        if (this.sharedReads.get(key) === shared$) this.sharedReads.delete(key);
      }),
      share({
        connector: () => new ReplaySubject<T>(1),
        resetOnError: true,
        resetOnComplete: true,
        resetOnRefCountZero: true,
      })
    );
    this.sharedReads.set(key, shared$);
    return shared$;
  }

  private localSearch$<
    T extends keyof IndexerSchema,
    const O extends SearchOptions<T>,
  >(
    table: T,
    query: Query<T>,
    options: O
  ): Observable<SearchResult<T, O>['nodes']> {
    return this.localRead$(
      () =>
        this.sharedRead$(readKey(['search', table, query, options]), () =>
          this.indexer
            .search$(table, query, options)
            .pipe(map((result: SearchResult<T, O>) => result.nodes))
        ),
      () => []
    );
  }

  private localAggregate$<
    T extends keyof IndexerSchema,
    const O extends AggregateOptions<T>,
  >(
    table: T,
    query: Query<T>,
    field: keyof IndexerSchema[T],
    options: O
  ): Observable<AggregateResult<T, O>['buckets']> {
    return this.localRead$(
      () =>
        this.sharedRead$(
          readKey(['aggregate', table, query, field, options]),
          () =>
            this.indexer.aggregate$(table, query, field, options).pipe(
              tap((result: AggregateResult<T, O>) =>
                this.localTruncated$.next(result.truncated === true)
              ),
              map(result => result.buckets)
            )
        ),
      () => []
    );
  }

  override dispose() {
    this.indexStateSubscription?.unsubscribe();
    super.dispose();
  }

  searchTitle$(query: string) {
    return this.localSearch$(
      'doc',
      {
        type: 'match',
        field: 'title',
        match: query,
      },
      {
        pagination: {
          skip: 0,
          limit: Infinity,
        },
      }
    ).pipe(map(nodes => nodes.map(node => node.id)));
  }

  /** Local mails whose subject, people or text match, best first. */
  searchMails$(query: string, limit: number) {
    return this.localSearch$(
      'mail',
      {
        type: 'boolean',
        occur: 'should',
        queries: (['subject', 'participants', 'content'] as const).map(
          field => ({ type: 'match' as const, field, match: query })
        ),
      },
      {
        fields: [
          'docId',
          'messageId',
          'accountId',
          'folderId',
          'subject',
          'participants',
          'snippet',
          'content',
          'receivedAt',
        ],
        pagination: { limit },
      }
    );
  }

  /** Local mails with the given message ids and their text versions. */
  mails$(messageIds?: readonly string[]) {
    return this.localSearch$(
      'mail',
      messageIds
        ? {
            type: 'boolean',
            occur: 'should',
            queries: messageIds.map(messageId => ({
              type: 'match' as const,
              field: 'messageId' as const,
              match: messageId,
            })),
          }
        : { type: 'all' },
      {
        fields: ['docId', 'messageId', 'contentHash'],
        pagination: { limit: messageIds ? messageIds.length : Infinity },
      }
    );
  }

  /** Docs whose title matches, with the match marked by `highlight`. */
  searchTitleHighlights$(
    query: string,
    highlight: { before: string; end: string }
  ) {
    return this.localSearch$(
      'doc',
      {
        type: 'match',
        field: 'title',
        match: query,
      },
      {
        fields: ['docId', 'title'],
        highlights: [{ field: 'title', ...highlight }],
      }
    );
  }

  /**
   * Blocks that link to `docId`, grouped by the doc they are in. A failed read
   * of the local index gives no backlinks; a failed server read reaches the
   * caller.
   */
  async aggregateBacklinks(docId: string, prefer: IndexerPreferOptions) {
    try {
      const { buckets } = await this.indexer.aggregate(
        'block',
        {
          type: 'boolean',
          occur: 'must',
          queries: [{ type: 'match', field: 'refDocId', match: docId }],
        },
        'docId',
        {
          hits: {
            fields: [
              'docId',
              'blockId',
              'parentBlockId',
              'parentFlavour',
              'additional',
              'markdownPreview',
            ],
            pagination: {
              limit: 5, // the max number of backlinks to show for each doc
            },
          },
          pagination: {
            limit: 100,
          },
          prefer,
        }
      );
      if (prefer === 'local') this.setIndexUnavailable(false);
      return buckets;
    } catch (error) {
      if (prefer === 'remote') throw error;
      this.setIndexUnavailable(true);
      return [];
    }
  }

  /** Every database block the local index knows. */
  watchDatabaseBlocks() {
    return this.localSearch$(
      'block',
      { type: 'match', field: 'flavour', match: 'affine:database' },
      {
        fields: ['docId', 'blockId', 'additional'],
        pagination: { limit: Infinity },
      }
    );
  }

  search$(
    query: string,
    options: DocsSearchOptions = {}
  ): Observable<DocsSearchResult[]> {
    if (options.prefer === 'remote') {
      return this.searchRemote$(query, options);
    }
    if (options.similar) return this.localSimilar$(query, options);

    return this.hiddenTechnicalDocIds$(options).pipe(
      switchMap(hiddenTechnicalDocIds =>
        combineLatest([
          this.localAggregate$(
            'block',
            this.createLocalSearchQuery(query),
            'docId',
            createDocsSearchAggregateOptions()
          ),
          this.localFileNameMatches$(query),
        ]).pipe(
          map(([buckets, fileNameMatches]) => {
            const indexedResults = mapDocsSearchAggregateBuckets(
              buckets,
              query,
              this.getDocTitle
            );
            return filterReadableDocs(
              filterHiddenTechnicalDocs(
                mergeDocsSearchAggregateItems(
                  mergeDocsSearchAggregateItems(
                    this.getLocalDocTitleMatches(query),
                    fileNameMatches
                  ),
                  indexedResults
                ),
                hiddenTechnicalDocIds,
                options
              ),
              this.readableDocs()
            );
          })
        )
      )
    );
  }

  private hiddenTechnicalDocIds$(options: DocsSearchOptions) {
    return options.includeTechnicalSources
      ? of(new Set<string>() as HiddenTechnicalDocIds)
      : from(this.getDefaultHiddenTechnicalDocIds());
  }

  /** Pages whose title or catalog file name is similar to the query. */
  private localSimilar$(query: string, options: DocsSearchOptions) {
    const docs = this.docsService.list.docs$.value;
    return combineLatest([
      this.hiddenTechnicalDocIds$(options),
      this.localSearch$(
        'doc',
        { type: 'exists', field: 'fileNames' },
        {
          fields: ['fileNames'],
          pagination: { skip: 0, limit: Math.max(docs.length, 1) },
        }
      ),
    ]).pipe(
      map(([hiddenTechnicalDocIds, nodes]) => {
        const fileNames = new Map(
          nodes.map(node => [node.id, [node.fields.fileNames ?? []].flat()])
        );
        return filterReadableDocs(
          filterHiddenTechnicalDocs(
            mapLocalDocSimilarMatches(
              query,
              docs.map(doc => ({
                docId: doc.id,
                title: doc.title$.value,
                trash: doc.trash$.value,
                fileNames: fileNames.get(doc.id),
              }))
            ),
            hiddenTechnicalDocIds,
            options
          ),
          this.readableDocs()
        );
      })
    );
  }

  private searchRemote$(query: string, options: DocsSearchOptions = {}) {
    const server = this.workspaceServerService.server;
    if (!server) {
      return throwError(() => new Error('Workspace server is not bound'));
    }

    const excludedPartitions = options.excludedPartitions?.length
      ? [...options.excludedPartitions]
      : undefined;
    const input: AggregateInput = {
      similar: options.similar === true,
      table: SearchTable.block,
      query: createServerAggregateQuery(query),
      field: 'docId',
      options: {
        ...createDocsSearchAggregateOptions(),
        includeTechnicalSources: options.includeTechnicalSources === true,
        excludedPartitions,
      },
    };

    const workspaceId = this.workspaceService.workspace.id;

    return from(
      Promise.all([
        server.gql({
          query: indexerAggregateQuery,
          variables: {
            id: workspaceId,
            input,
          },
        }) as Promise<IndexerAggregateQuery>,
        server.gql({
          query: indexerSearchDocsQuery,
          variables: {
            id: workspaceId,
            input: {
              similar: options.similar === true,
              keyword: query,
              limit: 50,
              includeTechnicalSources: options.includeTechnicalSources === true,
              excludedPartitions,
            },
          },
        }) as Promise<IndexerSearchDocsQuery>,
      ])
    ).pipe(
      map(([aggregateResult, searchDocsResult]) =>
        mergeRemoteDocsSearchResults(
          mapDocsSearchAggregateBuckets(
            aggregateResult.workspace.aggregate.buckets,
            query,
            this.getDocTitle
          ),
          mapRemoteSearchDocsForQuickSearch(
            searchDocsResult.workspace.searchDocs,
            query
          )
        )
      )
    );
  }

  /**
   * Combines the listed local answer and the server results of one query so
   * that every database row shard is answered by exactly one source (see
   * {@link mergeDatabaseSearchResults}). The local results are kept as they
   * are. Pass `null` when no server answered (offline, failed request): the
   * local results then also name the databases whose rows were not all
   * searched.
   */
  async mergeDatabaseSearch(
    local: DocsSearchResult[],
    remote: DocsSearchResult[] | null
  ): Promise<{ local: DocsSearchResult[]; remote: DocsSearchResult[] }> {
    const listedShardIds = new Set(
      local.flatMap(result =>
        (result.databaseShardHits ?? []).map(hit => hit.shardDocId)
      )
    );

    const remoteByShard = new Map<
      string,
      { version: string | undefined; hits: DocsSearchResult[] }
    >();
    for (const result of remote ?? []) {
      const shardDocId = result.databaseRowShardDocId;
      if (!shardDocId) continue;
      const entry = remoteByShard.get(shardDocId) ?? {
        version: result.databaseRowShardVersion,
        hits: [],
      };
      entry.hits.push(result);
      remoteByShard.set(shardDocId, entry);
    }

    const unlistedShardIds = [...remoteByShard.keys()].filter(
      id => !listedShardIds.has(id)
    );
    const localVersions = await this.getIndexedShardVersions(unlistedShardIds);
    const indexedShardIds = new Set(Object.keys(localVersions));

    const merged = mergeDatabaseSearchResults(
      {
        listedShardIds,
        indexedShardIds,
        unsyncedShardIds: await this.getUnsyncedShardIds(
          unlistedShardIds.filter(id => indexedShardIds.has(id))
        ),
        gaps: remote ? [] : await this.indexer.getDatabaseSearchGaps(),
      },
      remote ? { byShard: remoteByShard } : null,
      localVersions
    );

    const mergedLocal = [...local];

    const unsearched: DocsSearchResult[] = [];
    for (const gap of merged.unsearched) {
      const index = mergedLocal.findIndex(
        result =>
          result.sourceType === 'database' &&
          result.docId === gap.ownerDocId &&
          result.blockId === gap.blockId &&
          !result.databaseRowId
      );
      if (index >= 0) {
        mergedLocal[index] = {
          ...mergedLocal[index],
          databaseUnsearchedShardCount: gap.missingShardCount,
        };
        continue;
      }
      unsearched.push({
        docId: gap.ownerDocId,
        title: this.getDocTitle(gap.ownerDocId),
        score: 0,
        sourceType: 'database',
        blockId: gap.blockId,
        databaseUnsearchedShardCount: gap.missingShardCount,
      });
    }

    const keptRemote = new Set(merged.remoteHits);
    return {
      local: unsearched.length
        ? [
            ...mergedLocal,
            ...filterReadableDocs(
              filterHiddenTechnicalDocs(
                unsearched,
                await this.getDefaultHiddenTechnicalDocIds()
              ),
              this.readableDocs()
            ),
          ]
        : mergedLocal,
      remote: (remote ?? []).filter(
        result => !result.databaseRowShardDocId || keptRemote.has(result)
      ),
    };
  }

  /** Versions the local index holds for the given row shards. */
  private async getIndexedShardVersions(
    shardDocIds: readonly string[]
  ): Promise<Record<string, string>> {
    const workspaceId = this.workspaceService.workspace.id;
    const rootDocIds = new Set<string>();
    for (const shardDocId of shardDocIds) {
      const parts = parseServerDatabaseRowShardDocId(shardDocId, workspaceId);
      if (parts) rootDocIds.add(createServerDatabaseRootDocId(parts));
    }
    const versions: Record<string, string> = {};
    for (const rootDocId of rootDocIds) {
      const coverage = await this.indexer.getDatabaseSearchCoverage(rootDocId);
      for (const shardDocId of shardDocIds) {
        const version = coverage.shardVersions[shardDocId];
        if (version !== undefined) versions[shardDocId] = version;
      }
    }
    return versions;
  }

  /** Row shards with local changes the server has not confirmed. */
  private async getUnsyncedShardIds(
    shardDocIds: readonly string[]
  ): Promise<Set<string>> {
    const engine = this.workspaceService.workspace.engine.doc;
    const states = await Promise.all(
      shardDocIds.map(shardDocId =>
        firstValueFrom(engine.docState$(shardDocId))
      )
    );
    return new Set(
      shardDocIds.filter(
        (_, index) => !states[index].synced || states[index].updating
      )
    );
  }

  private createLocalSearchQuery(query: string): Query<'block'> {
    return {
      type: 'boolean',
      occur: 'must',
      queries: [
        {
          type: 'match',
          field: 'content',
          match: query,
        },
        {
          type: 'boolean',
          occur: 'should',
          queries: [
            {
              type: 'match',
              field: 'content',
              match: query,
            },
            {
              type: 'boost',
              boost: 1.5,
              query: {
                type: 'match',
                field: 'flavour',
                match: 'affine:page',
              },
            },
          ],
        },
      ],
    };
  }

  private readonly getDocTitle = (docId: string): string =>
    this.docsService.list.doc$(docId).value?.title$.value ?? '';

  /** Pages whose catalog file names match, whether or not their text is local. */
  private localFileNameMatches$(query: string) {
    if (!query.trim()) return of([]);
    return this.localSearch$(
      'doc',
      { type: 'match', field: 'fileNames', match: query },
      { fields: ['fileNames'], pagination: { skip: 0, limit: 50 } }
    ).pipe(
      map(nodes =>
        mapLocalDocFileNameMatches(
          query,
          nodes.map(node => ({
            docId: node.id,
            fileNames: [node.fields.fileNames ?? []].flat(),
          })),
          this.getDocTitle
        )
      )
    );
  }

  private readonly getLocalDocTitleMatches = (query: string) =>
    mapLocalDocTitleMatches(
      query,
      this.docsService.list.docs$.value.map(doc => ({
        docId: doc.id,
        title: doc.title$.value,
        trash: doc.trash$.value,
      }))
    );

  /**
   * The server names the hidden mailbox pages once per session; the answer is
   * kept for the next start. A local search reads the kept set and never
   * waits on the server; only the very first search on a device does.
   */
  private async getDefaultHiddenTechnicalDocIds(): Promise<HiddenTechnicalDocIds> {
    const server = this.workspaceServerService.server;
    if (!server) return new Set();

    this.hiddenTechnicalDocIdsPromise ??= (
      server.gql({
        query: mailboxRootDocIdsQuery,
        variables: { workspaceId: this.workspaceService.workspace.id },
      }) as Promise<MailboxRootDocIdsQuery>
    )
      .then((result: MailboxRootDocIdsQuery) => {
        this.localCache.set(
          HIDDEN_TECHNICAL_DOC_IDS_KEY,
          result.mailboxRootDocIds
        );
        return new Set(result.mailboxRootDocIds);
      })
      .catch(error => {
        console.warn(
          '[DocsSearchService] Failed to resolve hidden technical docs for local search',
          error
        );
        return new Set<string>();
      });

    const kept = this.localCache.get<string[]>(HIDDEN_TECHNICAL_DOC_IDS_KEY);
    return kept ? new Set(kept) : this.hiddenTechnicalDocIdsPromise;
  }

  watchRefsFrom(ids: string | string[]) {
    const docIds = Array.isArray(ids) ? ids : [ids];
    if (docIds.length === 0) {
      return of([]);
    }

    return this.localSearch$(
      'block',
      {
        type: 'boolean',
        occur: 'must',
        queries: [
          {
            type: 'boolean',
            occur: 'should',
            queries: docIds.map(id => ({
              type: 'match',
              field: 'docId',
              match: id,
            })),
          },
          {
            type: 'exists',
            field: 'refDocId',
          },
        ],
      },
      {
        fields: ['refDocId', 'ref'],
        pagination: {
          limit: 100,
        },
      }
    ).pipe(
      switchMap(nodes => {
        return fromPromise(async () => {
          const refs = Array.from(
            new Map(
              nodes
                .flatMap((node: { fields: { ref?: string | string[] } }) => {
                  const { ref } = node.fields;
                  return typeof ref === 'string'
                    ? [JSON.parse(ref)]
                    : (ref?.map((item: string) => JSON.parse(item)) ?? []);
                })
                .filter((ref: { docId: string }) => !docIds.includes(ref.docId))
                .map((ref: { docId: string }) => [ref.docId, ref] as const)
            ).values()
          ) as ({ docId: string } & ReferenceParams)[];

          return refs
            .flatMap((ref: { docId: string }) => {
              const doc = this.docsService.list.doc$(ref.docId).value;
              if (!doc) return null;

              const title = doc.title$.value;
              const params = omit(ref, ['docId']);

              return {
                title,
                docId: doc.id,
                params: isEmpty(params) ? undefined : toDocSearchParams(params),
              };
            })
            .filter(ref => !!ref);
        });
      })
    );
  }

  watchDatabasesTo(docId: string) {
    const DatabaseAdditionalSchema = z.object({
      databaseName: z.string().optional(),
    });
    return this.localSearch$(
      'block',
      {
        type: 'boolean',
        occur: 'must',
        queries: [
          {
            type: 'match',
            field: 'refDocId',
            match: docId,
          },
          {
            type: 'match',
            field: 'parentFlavour',
            match: 'affine:database',
          },
        ],
      },
      {
        fields: ['docId', 'blockId', 'parentBlockId', 'additional'],
        pagination: {
          limit: 100,
        },
      }
    ).pipe(
      map(nodes => {
        return nodes
          .map((node: { fields: Record<string, string | string[]> }) => {
            if (node.fields.docId === docId) {
              // Ignore if it is a link to the current document.
              return null;
            }

            const additional =
              typeof node.fields.additional === 'string'
                ? node.fields.additional
                : node.fields.additional[0];

            return {
              docId:
                typeof node.fields.docId === 'string'
                  ? node.fields.docId
                  : node.fields.docId[0],
              rowId:
                typeof node.fields.blockId === 'string'
                  ? node.fields.blockId
                  : node.fields.blockId[0],
              databaseBlockId:
                typeof node.fields.parentBlockId === 'string'
                  ? node.fields.parentBlockId
                  : node.fields.parentBlockId[0],
              databaseName: DatabaseAdditionalSchema.safeParse(additional).data
                ?.databaseName as string | undefined,
            };
          })
          .filter(
            (item: unknown): item is NonNullable<typeof item> => item !== null
          );
      })
    );
  }

  /** The indexed text of one block, empty until the block is indexed. */
  watchBlockText(docId: string, blockId: string): Observable<string> {
    return this.localSearch$(
      'block',
      {
        type: 'boolean',
        occur: 'must',
        queries: [
          { type: 'match', field: 'docId', match: docId },
          { type: 'match', field: 'blockId', match: blockId },
        ],
      },
      {
        fields: ['content'],
        pagination: {
          limit: 1,
        },
      }
    ).pipe(
      map(nodes => {
        const content = nodes.at(0)?.fields.content;
        return (typeof content === 'string' ? content : content?.[0]) ?? '';
      })
    );
  }

  watchDocSummary(docId: string) {
    return this.localSearch$(
      'doc',
      {
        type: 'match',
        field: 'docId',
        match: docId,
      },
      {
        fields: ['summary'],
        pagination: {
          limit: 1,
        },
      }
    ).pipe(
      map(nodes => {
        const node = nodes.at(0);
        return (
          (typeof node?.fields.summary === 'string'
            ? node?.fields.summary
            : node?.fields.summary[0]) ?? null
        );
      })
    );
  }
}
